/* The Whisper engine behind useDictation: record the mic, cut at pauses, send
   each chunk to the Worker, append its text in order; on stop, tidy the
   dictated span.

   Append-only by construction — one chunk in, one string out, nothing to
   reconcile — which is why the phrase-doubling of the browser engine cannot
   recur here. The cost is that text arrives a phrase at a time, a second or
   two after a pause, rather than word by word.

   Each cut STOPS the recorder and starts a new one on the same stream. A
   `requestData` slice of a running WebM stream has no container header and
   does not decode on its own; a fresh recorder per chunk gives Whisper a
   complete file every time. */
import { chunkStart, chunkStep, rmsLevel, voiceLevel, type ChunkState } from '@/lib/dictation-chunk';
import { transcribeChunk, tidyDictation } from '@/data/client';

export interface WhisperCallbacks {
  onText(next: string): void;
  /** A chunk is being transcribed, or the final tidy is running. */
  onPending(pending: boolean): void;
  onError(message: string): void;
  /** How loud the speaker is above the room, 0..1, ten times a second — the "it hears me" cue. */
  onLevel(level: number): void;
}

/* Chrome, Edge, Firefox and Android record Opus in WebM; Safari records AAC in
   MP4. The Worker accepts either. */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

export function recordingSupported(): boolean {
  return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined' && MIME_CANDIDATES.some((m) => MediaRecorder.isTypeSupported(m));
}

function micMessage(e: unknown): string {
  const name = (e as { name?: string })?.name ?? '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Microphone access is blocked. Allow the mic for this site, or type your answer.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No microphone was found. Check your mic, or type your answer instead.';
  return 'Could not start voice input. Please type your answer.';
}

interface Current { finish(send: boolean): Promise<void> }

export class WhisperDictation {
  private base = '';
  private segments: string[] = [];
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private current: Current | null = null;
  private chunk: ChunkState = chunkStart(0);
  private timer: number | null = null;
  private queue: Promise<void> = Promise.resolve();
  private inflight = 0;
  private stopped = false;
  private done: Promise<string> | null = null;
  private mime = '';

  constructor(private cb: WhisperCallbacks) {}

  /** Resolves once the mic is open; rejects (after reporting) when it is not. */
  async start(current: string): Promise<void> {
    this.base = current ? `${current} ` : '';
    this.segments = [];
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      this.cb.onError(micMessage(e));
      throw e;
    }
    this.mime = MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
    this.ctx = new AudioContext();
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    this.ctx.createMediaStreamSource(this.stream).connect(this.analyser);
    this.beginChunk();
    const buf = new Uint8Array(this.analyser.fftSize);
    this.timer = window.setInterval(() => {
      if (!this.analyser || this.stopped) return;
      this.analyser.getByteTimeDomainData(buf);
      const r = chunkStep(this.chunk, rmsLevel(buf), performance.now());
      this.chunk = r.state;
      this.cb.onLevel(voiceLevel(r.state));
      if (r.cut) {
        void this.current?.finish(r.cut === 'send');
        this.beginChunk();
      }
    }, 100);
  }

  private beginChunk(): void {
    const stream = this.stream;
    if (!stream || this.stopped) return;
    const rec = new MediaRecorder(stream, this.mime ? { mimeType: this.mime } : undefined);
    const startedAt = performance.now();
    let send = false;
    let settle: () => void = () => {};
    const done = new Promise<void>((res) => { settle = res; });
    // ondataavailable precedes onstop, so by the time `done` resolves the chunk is queued.
    rec.ondataavailable = (e) => { if (send && e.data.size > 0) this.enqueue(e.data, performance.now() - startedAt); };
    rec.onstop = () => settle();
    rec.onerror = () => settle();
    rec.start();
    // The first chunk learns the room; later ones inherit it.
    this.chunk = this.current ? chunkStart(startedAt, this.chunk.floor) : chunkStart(startedAt);
    this.current = {
      finish: (s) => {
        send = s;
        if (rec.state === 'inactive') settle(); else rec.stop();
        return done;
      },
    };
  }

  private enqueue(blob: Blob, ms: number): void {
    this.inflight++;
    this.cb.onPending(true);
    this.queue = this.queue.then(async () => {
      try {
        const text = await transcribeChunk(blob, ms);
        if (text) { this.segments.push(text); this.cb.onText(this.base + this.segments.join(' ')); }
      } catch {
        this.cb.onError('The voice service is unavailable right now. Please type the rest of your answer.');
      } finally {
        this.inflight--;
        if (this.inflight === 0 && !this.stopped) this.cb.onPending(false);
      }
    });
  }

  /** Stop recording; when the last chunk is back and tidied, resolve with the
      text the box now holds. Send awaits this so pressing it mid-sentence
      sends the whole answer rather than losing the last phrase — and so no
      late transcript lands in a box that was just cleared. */
  stop(): Promise<string> {
    if (this.done) return this.done;
    this.stopped = true;
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    const last = this.current?.finish(this.chunk.speech) ?? Promise.resolve();
    this.current = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.analyser = null;
    this.cb.onLevel(0);
    this.cb.onPending(true);
    this.done = last.then(() => this.queue).then(async () => {
      const spoken = this.segments.join(' ').trim();
      let text = this.base + spoken;
      if (spoken) {
        try {
          const r = await tidyDictation(spoken);
          if (r.text) { text = this.base + r.text; this.cb.onText(text); }
        } catch { /* the raw transcript is already in the box */ }
      }
      this.cb.onPending(false);
      return text;
    });
    return this.done;
  }
}
